import { useRef, useState, type KeyboardEvent } from "react";
import type { DatasetField, DatasetRow, DatasetValue } from "@/api/datasets";
import { Checkbox } from "@/components/ui";
import { useT } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { cn } from "@/lib/cn";
import { DatasetCellBox } from "./dataset-cell-box";
import { DatasetCellEditor, type CellMove } from "./dataset-cell-editor";
import {
  DatasetFieldMenu,
  type DatasetFieldActions,
} from "./dataset-field-menu";
import { columnCap, columnTrack } from "./dataset-columns";
import { DatasetRowMenu } from "./dataset-row-menu";
import { cellPlaceholder, cellText, isNumericField } from "./dataset-values";
import type { DatasetCellRef, DatasetEditOutcome } from "./use-dataset-page";

const MOVES: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

/**
 * The pinned first column paints the window's own surface under it and
 * starts at the frame's inset, the title's edge.
 */
const PINNED =
  "sticky left-0 z-[1] bg-[var(--frame-paint,var(--card-surface))] pl-[var(--open-pad-inline,0.75rem)]";

/**
 * A Data frame's page of rows as a grid (`role="grid"`): muted field labels,
 * 36px rows, numbers right-aligned, the first field pinned while the rest
 * scroll sideways. Each column is as wide as its content within its type's
 * bounds (rows share the tracks through subgrid); a longer value ends in an
 * ellipsis and shows in full on hover, in the same box over the cell that
 * editing it opens (`DatasetCellBox`). An empty cell shows faintly what it
 * takes (its field's description, else "Add {field}"); a click edits a
 * cell, and hovering one outlines it. A focused cell takes the keys
 * (arrows and Tab move, Enter edits or toggles, Delete and Backspace clear,
 * Escape leaves) and none of them reach the wall's or Focus's shortcuts.
 * Each field's header holds its menu (rename, delete, remove an option);
 * each row's end holds its menu (Delete row).
 */
export function DatasetGrid({
  title,
  fields,
  canDeleteField,
  rows,
  total,
  offset,
  conflict,
  onEdit,
  onDelete,
  fieldActions,
}: {
  title: string;
  /** The fields shown as columns. */
  fields: readonly DatasetField[];
  /** False while the table has one field: it keeps at least one. */
  canDeleteField: boolean;
  rows: readonly DatasetRow[];
  /** The table's row count, for `aria-rowcount`. */
  total: number;
  /** The page's first row in the table. */
  offset: number;
  /** The cell whose save lost to a newer change. */
  conflict: DatasetCellRef | null;
  onEdit: (
    rowId: string,
    fieldId: string,
    value: DatasetValue,
  ) => Promise<DatasetEditOutcome>;
  onDelete: (rowId: string) => void;
  fieldActions: DatasetFieldActions;
}) {
  const t = useT(framesDatasetMessages);
  const gridRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<[number, number]>([0, 0]);
  const [editing, setEditing] = useState<DatasetCellRef | null>(null);
  // The hovered cell whose text ends in an ellipsis, shown in full.
  const [preview, setPreview] = useState<DatasetCellRef | null>(null);
  // The fields, the row menu, then the rest of the width (at least the inset).
  const columns = [
    ...fields.map((field) => columnTrack(field.type)),
    "2.25rem",
    "minmax(var(--open-pad-inline,0.75rem), 1fr)",
  ].join(" ");
  const placeholders = fields.map(cellPlaceholder);
  // A deleted row or field, or a shorter page, never leaves the grid without
  // a tab stop.
  const activeRow = Math.min(active[0], rows.length - 1);
  const activeCol = Math.min(active[1], fields.length - 1);

  const focusCell = (row: number, col: number) => {
    const r = Math.max(0, Math.min(rows.length - 1, row));
    const c = Math.max(0, Math.min(fields.length - 1, col));
    setActive([r, c]);
    gridRef.current
      ?.querySelector<HTMLElement>(`[data-cell="${r}:${c}"]`)
      ?.focus();
  };

  const edit = (cell: DatasetCellRef) => {
    setPreview(null);
    setEditing(cell);
  };

  /**
   * Closes the editor and saves a changed value. `move`: focus stays on the
   * cell or goes right (Tab); `none` when the person left for elsewhere.
   */
  const save = (
    row: number,
    col: number,
    value: DatasetValue,
    move: CellMove = "stay",
  ) => {
    const rowId = rows[row]?.id;
    const fieldId = fields[col]?.id;
    setEditing(null);
    if (move !== "none") focusCell(row, move === "next" ? col + 1 : col);
    const before =
      rowId && fieldId ? (rows[row]!.values[fieldId] ?? null) : null;
    if (rowId && fieldId && before !== value)
      void onEdit(rowId, fieldId, value);
  };

  const onCellKey = (
    e: KeyboardEvent<HTMLDivElement>,
    row: number,
    col: number,
  ) => {
    if (e.target !== e.currentTarget) return;
    const field = fields[col]!;
    const value = rows[row]?.values[field.id] ?? null;
    const move = MOVES[e.key];
    if (move) {
      e.preventDefault();
      focusCell(row + move[0], col + move[1]);
    } else if (e.key === "Tab" && !e.shiftKey && col < fields.length - 1) {
      e.preventDefault();
      focusCell(row, col + 1);
    } else if (e.key === "Tab" && e.shiftKey && col > 0) {
      e.preventDefault();
      focusCell(row, col - 1);
    } else if (
      e.key === "Enter" ||
      (e.key === " " && field.type === "boolean")
    ) {
      e.preventDefault();
      if (field.type === "boolean") save(row, col, value !== true);
      else edit({ rowId: rows[row]!.id, fieldId: field.id });
    } else if (
      (e.key === "Delete" || e.key === "Backspace") &&
      value !== null
    ) {
      e.preventDefault();
      save(row, col, null);
    } else if (e.key === "Escape") {
      e.currentTarget.blur();
    }
  };

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={t("tableLabel", { title })}
      aria-rowcount={total + 1}
      aria-colcount={fields.length + 1}
      className="grid w-max min-w-full text-appkit-body"
      style={{ gridTemplateColumns: columns }}
      // Every key in the grid stays here: the wall's and Focus's shortcuts
      // must not delete a frame while a cell is being typed in.
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        role="row"
        aria-rowindex={1}
        className="col-span-full grid grid-cols-subgrid"
      >
        {fields.map((field, col) => (
          <div
            key={field.id}
            role="columnheader"
            aria-colindex={col + 1}
            className={cn(
              "group/header flex min-w-0 items-center gap-1 py-1 pl-3 pr-1 text-appkit-caption font-semibold uppercase tracking-wide text-muted",
              columnCap(field.type),
              isNumericField(field.type) &&
                "flex-row-reverse pl-1 pr-3 text-right",
              col === 0 && PINNED,
            )}
          >
            <span
              className="min-w-0 flex-1 truncate"
              title={field.name}
              data-testid={`dataset-header-${field.id}`}
            >
              {field.name}
            </span>
            <DatasetFieldMenu
              field={field}
              canDelete={canDeleteField}
              actions={fieldActions}
            />
          </div>
        ))}
        <div role="columnheader" aria-colindex={fields.length + 1} />
      </div>
      {rows.map((row, r) => (
        <div
          key={row.id}
          role="row"
          aria-rowindex={offset + r + 2}
          className="group col-span-full grid min-h-9 grid-cols-subgrid items-center hover:bg-[var(--color-interactive-hover)]"
          data-testid={`dataset-row-${row.id}`}
        >
          {fields.map((field, c) => {
            const value = row.values[field.id] ?? null;
            const isEditing =
              editing?.rowId === row.id && editing.fieldId === field.id;
            const previewing =
              preview?.rowId === row.id && preview.fieldId === field.id;
            const open = isEditing || previewing;
            const conflicted =
              conflict?.rowId === row.id && conflict.fieldId === field.id;
            const text = cellText(value, field.type, field.options);
            // A checkbox shows its own state; every other cell edits on click.
            const editable = field.type !== "boolean";
            return (
              <div
                key={field.id}
                role="gridcell"
                aria-colindex={c + 1}
                tabIndex={activeRow === r && activeCol === c ? 0 : -1}
                className={cn(
                  "relative flex min-h-9 min-w-0 items-center px-3 text-primary outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent",
                  columnCap(field.type),
                  isNumericField(field.type) &&
                    "justify-end text-right tabular-nums",
                  c === 0 && PINNED,
                  editable && !isEditing && "cursor-text",
                  editable &&
                    !isEditing &&
                    !conflicted &&
                    "hover:ring-1 hover:ring-inset hover:ring-input-stroke",
                  conflicted && "ring-1 ring-inset ring-warning",
                  // Above the pinned column and the rows below it.
                  open && "z-[2]",
                )}
                onFocus={() => setActive([r, c])}
                onClick={(e) => {
                  // A click inside a portalled picker is not on this cell,
                  // and one that ends selecting text is for copying.
                  if (
                    editable &&
                    !isEditing &&
                    e.currentTarget.contains(e.target as Node) &&
                    !window.getSelection()?.toString()
                  )
                    edit({ rowId: row.id, fieldId: field.id });
                }}
                onMouseEnter={(e) => {
                  const shown =
                    e.currentTarget.querySelector<HTMLElement>(
                      "[data-cell-text]",
                    );
                  if (shown && shown.scrollWidth > shown.clientWidth)
                    setPreview({ rowId: row.id, fieldId: field.id });
                }}
                onMouseLeave={() => setPreview(null)}
                onKeyDown={(e) => onCellKey(e, r, c)}
                data-cell={`${r}:${c}`}
                data-conflict={conflicted || undefined}
                data-testid={`dataset-cell-${row.id}-${field.id}`}
              >
                {field.type === "boolean" ? (
                  <Checkbox
                    checked={value === true}
                    onCheckedChange={(checked) => save(r, c, checked)}
                    tabIndex={-1}
                    aria-label={t("editCell", { field: field.name })}
                    data-testid={`dataset-cell-toggle-${row.id}-${field.id}`}
                  />
                ) : (
                  <>
                    {/* Kept under an open cell: its column keeps its width. */}
                    <span
                      className={cn(
                        "truncate",
                        !text && "text-subtle",
                        open && "invisible",
                      )}
                      data-cell-text
                      data-testid={
                        text
                          ? undefined
                          : `dataset-cell-placeholder-${row.id}-${field.id}`
                      }
                    >
                      {text || placeholders[c]}
                    </span>
                    {isEditing ? (
                      <DatasetCellEditor
                        field={field}
                        value={value}
                        onSave={(next, move) => save(r, c, next, move)}
                        onCancel={(refocus) => {
                          setEditing(null);
                          if (refocus) focusCell(r, c);
                        }}
                      />
                    ) : (
                      previewing && (
                        <DatasetCellBox
                          readOnly
                          tabIndex={-1}
                          aria-hidden
                          value={text || placeholders[c]}
                          className={cn(!text && "text-subtle")}
                          data-testid={`dataset-cell-preview-${row.id}-${field.id}`}
                        />
                      )
                    )}
                  </>
                )}
              </div>
            );
          })}
          <div role="gridcell" aria-colindex={fields.length + 1}>
            <DatasetRowMenu rowId={row.id} onDelete={onDelete} />
          </div>
        </div>
      ))}
    </div>
  );
}
